import type { ConsentManagerConfig, Translation } from '@/core/types';
import { BannerStyles } from './banner-styles';

export class ConsentBanner {
  private element: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: ConsentManagerConfig;
  private translation: Translation;
  private onAcceptAll: () => void;
  private onRejectAll: () => void;
  private onCustomize: () => void;
  private isVisible = false;

  constructor(
    config: ConsentManagerConfig,
    translation: Translation,
    callbacks: {
      onAcceptAll: () => void;
      onRejectAll: () => void;
      onCustomize: () => void;
    }
  ) {
    this.config = config;
    this.translation = translation;
    this.onAcceptAll = callbacks.onAcceptAll;
    this.onRejectAll = callbacks.onRejectAll;
    this.onCustomize = callbacks.onCustomize;
  }

  public show(): void {
    if (this.isVisible) return;

    this.createElement();
    this.attachToDOM();
    this.isVisible = true;

    requestAnimationFrame(() => {
      this.element?.classList.add('cmp-banner--visible');
    });
  }

  public hide(): void {
    if (!this.isVisible || !this.element) return;

    this.element.classList.remove('cmp-banner--visible');

    setTimeout(() => {
      this.removeFromDOM();
      this.isVisible = false;
    }, 300);
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.className = `cmp-banner cmp-banner--${this.config.position || 'bottom'}`;
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-label', this.translation.banner.title);
    this.element.setAttribute('aria-modal', 'false');

    this.shadowRoot = this.element.attachShadow({ mode: 'closed' });

    const styles = document.createElement('style');
    styles.textContent = BannerStyles.getStyles(this.config);

    const content = document.createElement('div');
    content.className = 'cmp-banner__container';
    content.innerHTML = this.getHTML();

    this.shadowRoot.appendChild(styles);
    this.shadowRoot.appendChild(content);

    this.attachEventListeners();
  }

  private getHTML(): string {
    const showRejectAll = this.config.showRejectAll !== false;
    const showCustomize = this.config.showCustomize !== false;

    return `
      <div class="cmp-banner__content">
        <div class="cmp-banner__text">
          <h2 class="cmp-banner__title">${this.escapeHTML(this.translation.banner.title)}</h2>
          <p class="cmp-banner__description">${this.escapeHTML(this.translation.banner.description)}</p>
        </div>
        <div class="cmp-banner__actions">
          <button 
            type="button" 
            class="cmp-banner__button cmp-banner__button--accept" 
            data-action="accept-all"
            aria-label="${this.escapeHTML(this.translation.banner.acceptAll)}"
          >
            ${this.escapeHTML(this.translation.banner.acceptAll)}
          </button>
          ${
            showRejectAll
              ? `
            <button 
              type="button" 
              class="cmp-banner__button cmp-banner__button--reject" 
              data-action="reject-all"
              aria-label="${this.escapeHTML(this.translation.banner.rejectAll)}"
            >
              ${this.escapeHTML(this.translation.banner.rejectAll)}
            </button>
          `
              : ''
          }
          ${
            showCustomize
              ? `
            <button 
              type="button" 
              class="cmp-banner__button cmp-banner__button--customize" 
              data-action="customize"
              aria-label="${this.escapeHTML(this.translation.banner.customize)}"
            >
              ${this.escapeHTML(this.translation.banner.customize)}
            </button>
          `
              : ''
          }
        </div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    if (!this.shadowRoot) return;

    const container = this.shadowRoot.querySelector('.cmp-banner__container');
    if (!container) return;

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const actionElement = target.closest('[data-action]') as HTMLElement;
      const action = actionElement?.getAttribute('data-action');

      if (!action) return;

      event.preventDefault();
      event.stopPropagation();

      switch (action) {
        case 'accept-all':
          this.onAcceptAll();
          break;
        case 'reject-all':
          this.onRejectAll();
          break;
        case 'customize':
          this.onCustomize();
          break;
      }
    });

    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.isVisible) return;

    if (event.key === 'Escape') {
      this.hide();
      return;
    }

    if (event.key === 'Tab') {
      this.handleTabNavigation(event);
    }
  }

  private handleTabNavigation(event: KeyboardEvent): void {
    if (!this.shadowRoot) return;

    const focusableElements = this.shadowRoot.querySelectorAll(
      'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  private attachToDOM(): void {
    if (!this.element) return;

    const existingBanner = document.querySelector('.cmp-banner');
    if (existingBanner) {
      existingBanner.remove();
    }

    document.body.appendChild(this.element);

    setTimeout(() => {
      const firstButton = this.shadowRoot?.querySelector(
        'button'
      ) as HTMLElement;
      firstButton?.focus();
    }, 100);
  }

  private removeFromDOM(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.shadowRoot = null;

    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private escapeHTML(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  public updateTranslation(translation: Translation): void {
    this.translation = translation;
    if (this.isVisible) {
      this.hide();
      setTimeout(() => this.show(), 350);
    }
  }

  public destroy(): void {
    this.hide();
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }
}
